import React, { useMemo } from 'react';
/**
 * Recursively applies border radius styles to DNAButton components within a button group.
 *
 * This function traverses the React component tree to find DNAButton and DNAContextMenu components
 * and applies appropriate border radius styles to create a seamless button group appearance.
 *
 * @param child - The React element to process
 * @param borderRadiusStyle - The border radius style object to apply (e.g., { borderTopRightRadius: 0 })
 * @returns A new React element with applied border radius styles, or the original element if no styling is needed
 */
function applyBorderRadiusRecursively(child, borderRadiusStyle) {
    if (child.type?.displayName === 'DNAButton') {
        const childStyle = child.props?.style || {};
        return React.cloneElement(child, {
            ...child.props,
            style: [childStyle, borderRadiusStyle],
        });
    }
    else if (child.type?.displayName === 'DNAContextMenu') {
        const DNAContextMenuChildrenClone = Array.isArray(child.props.children)
            ? child.props.children?.map((contextMenuChild) => {
                if (contextMenuChild &&
                    contextMenuChild?.type?.displayName === 'DNAContextMenuAnchor' &&
                    !Array.isArray(contextMenuChild?.props.children) &&
                    contextMenuChild?.props.children?.type?.displayName === 'DNAButton') {
                    const isCaretDownIconButton = contextMenuChild?.props.children?.props?.iconLeft === 'caret-down';
                    const isXLButton = contextMenuChild?.props.children?.props?.size === 'xl';
                    const iconStyle = contextMenuChild?.props.children?.props?.iconStyle || {};
                    const defaultIconDimension = isXLButton ? { height: 16, width: 16 } : { height: 12, width: 12 };
                    const overwriteIconStyle = isCaretDownIconButton ? [defaultIconDimension, iconStyle] : iconStyle;
                    return React.cloneElement(contextMenuChild, {
                        ...contextMenuChild.props,
                        children: React.cloneElement(contextMenuChild.props.children, {
                            ...(contextMenuChild.props.children?.props || {}),
                            style: [contextMenuChild.props.children.props.style, borderRadiusStyle],
                            iconStyle: overwriteIconStyle,
                        }),
                    });
                }
                else
                    return contextMenuChild;
            })
            : child.props.children;
        return React.cloneElement(child, {
            ...child.props,
            children: DNAContextMenuChildrenClone,
        });
    }
    else if (child.props?.children) {
        const childrenArray = React.Children.toArray(child.props.children);
        // If there's more than one child, don't recurse - return unchanged
        if (childrenArray.length > 1)
            return child;
        // If there's exactly one child and it's a valid element, recurse into it
        if (childrenArray.length === 1 && React.isValidElement(childrenArray[0])) {
            const processedChild = applyBorderRadiusRecursively(childrenArray[0], borderRadiusStyle);
            return React.cloneElement(child, {
                ...child.props,
                children: processedChild,
            });
        }
    }
    return child;
}
/**
 * Custom hook that processes button group children to apply seamless border radius styling.
 *
 * This hook takes an array of React children (typically DNAButton and DNAContextMenu components)
 * and applies border radius styles to create a cohesive button group where adjacent buttons
 * share borders without gaps. The styling is applied based on the button's position in the group
 * and the specified appearance (row or column layout).
 *
 * @param props - The hook parameters
 * @param props.children - React children to process (DNAButton, DNAContextMenu, or other components)
 * @param props.appearance - Layout direction: 'row' for horizontal, 'col' for vertical
 * @returns An array of React elements with applied border radius styles
 */
export const useButtonGroup = ({ children, appearance }) => {
    return useMemo(() => {
        const childrenArray = React.Children.toArray(children);
        const totalChildren = childrenArray.length;
        return childrenArray.map((child, index) => {
            if (!React.isValidElement(child) || totalChildren <= 1)
                return child;
            // Determine border radius style based on position
            let borderRadiusStyle;
            if (appearance === 'row') {
                if (index === 0 && totalChildren > 1) {
                    borderRadiusStyle = { borderTopRightRadius: 0, borderBottomRightRadius: 0 };
                }
                else if (index === totalChildren - 1) {
                    borderRadiusStyle = { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 };
                }
                else {
                    borderRadiusStyle = {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    };
                }
            }
            else {
                if (index === 0) {
                    borderRadiusStyle = { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 };
                }
                else if (index === totalChildren - 1) {
                    borderRadiusStyle = { borderTopLeftRadius: 0, borderTopRightRadius: 0 };
                }
                else {
                    borderRadiusStyle = {
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                    };
                }
            }
            return applyBorderRadiusRecursively(child, borderRadiusStyle);
        });
    }, [children, appearance]);
};
//# sourceMappingURL=useButtonGroup.js.map